import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/shadcn/ui/dialog";
import { Button } from "components/shadcn/ui/button";
import { HiOutlineX } from "react-icons/hi";
import { Copy, Check } from "lucide-react";
import { toast, Notification } from "components/ui";
import useDarkMode from "utils/hooks/useDarkMode";

const DetailDialog = ({
  isOpen,
  onClose,
  title,
  description,
  data,
  fields,
  actions,
  badges,
}) => {
  const [copiedField, setCopiedField] = useState(null);
  const [hoveredField, setHoveredField] = useState(null);
  const [isDark] = useDarkMode();

  // This dialog styles its inner content with inline `style` (not Tailwind/sx),
  // so dark mode is threaded explicitly here. The shadcn DialogContent bg flips
  // dark, so the heading/value/box colours below must flip too or they'd be
  // dark-on-dark / light islands. Mid-greys (#9ca3af) and semantics (#10b981)
  // read fine in both and are left as-is.
  const c = {
    border: isDark ? "#374151" : "#e5e7eb",
    borderSubtle: isDark ? "#374151" : "#f3f4f6",
    heading: isDark ? "#f1f5f9" : "#111827",
    value: isDark ? "#e5e7eb" : "#1f2937",
    desc: isDark ? "#9ca3af" : "#6b7280",
    icon: isDark ? "#9ca3af" : "#374151",
    boxBg: isDark ? "#1f2937" : "#f9fafb",
    boxBgHover: isDark ? "#374151" : "#f3f4f6",
  };

  if (!data) return null;

  const getFieldValue = (field) => {
    if (field.value) return field.value;
    if (field.key) {
      const keys = field.key.split('.');
      let value = data;
      for (const k of keys) {
        value = value?.[k];
      }
      return value;
    }
    return null;
  };

  const handleCopyField = async (field, index) => {
    let valueToCopy;

    if (field.render) {
      valueToCopy = field.render(data);
    } else {
      valueToCopy = getFieldValue(field);
    }

    if (valueToCopy === null || valueToCopy === undefined || valueToCopy === '') {
      return;
    }

    const textToCopy = typeof valueToCopy === 'string' ? valueToCopy : String(valueToCopy);

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedField(index);
      toast.push(<Notification type="success" duration={2000}>Copied to clipboard</Notification>);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.push(<Notification type="danger">Failed to copy</Notification>);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden flex flex-col min-w-[450px] max-w-[650px] p-0">
        {/* Close Button - Fixed position */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            right: '10px',
            top: '10px',
            zIndex: 50,
            padding: '6px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.1)',
            border: 'none',
            cursor: 'pointer',
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.2)'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.1)'}
        >
          <HiOutlineX style={{ width: '16px', height: '16px', color: c.icon }} />
        </button>

        {/* Header */}
        <div style={{ padding: '12px 16px', paddingRight: '48px', borderBottom: `1px solid ${c.border}` }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: c.heading, margin: 0 }}>
            {title}
          </h2>
          {description && (
            <p style={{ fontSize: '13px', color: c.desc, marginTop: '2px' }}>
              {description}
            </p>
          )}
          {/* Badges */}
          {badges && badges.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className={badge.className}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    fontWeight: '500',
                  }}
                >
                  {badge.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {fields.map((field, index) => {
              const displayValue = field.render ? field.render(data) : (field.value || getFieldValue(field));
              const hasValue = displayValue !== null && displayValue !== undefined && displayValue !== '' && displayValue !== 'N/A';
              const isHovered = hoveredField === index;
              const isCopied = copiedField === index;

              return (
                <div
                  key={index}
                  onClick={() => hasValue && handleCopyField(field, index)}
                  onMouseEnter={() => setHoveredField(index)}
                  onMouseLeave={() => setHoveredField(null)}
                  style={{
                    gridColumn: field.fullWidth ? 'span 2' : 'span 1',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${isHovered && hasValue ? c.border : c.borderSubtle}`,
                    backgroundColor: isHovered && hasValue ? c.boxBgHover : c.boxBg,
                    cursor: hasValue ? 'pointer' : 'default',
                    transition: 'all 0.15s ease',
                    position: 'relative',
                  }}
                >
                  <dt style={{ fontSize: '10px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{field.label}</span>
                    {hasValue && (
                      <span
                        style={{
                          opacity: isHovered || isCopied ? 1 : 0,
                          transition: 'opacity 0.15s ease',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {isCopied ? (
                          <Check style={{ width: '12px', height: '12px', color: '#10b981' }} />
                        ) : (
                          <Copy style={{ width: '12px', height: '12px', color: '#9ca3af' }} />
                        )}
                      </span>
                    )}
                  </dt>
                  <dd style={{ fontSize: '13px', fontWeight: '500', color: c.value, margin: 0 }}>
                    {displayValue || (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not provided</span>
                    )}
                  </dd>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions Footer */}
        {actions && actions.length > 0 && (
          <div style={{ borderTop: `1px solid ${c.border}`, padding: '10px 16px', backgroundColor: c.boxBg }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
              {actions.map((action, index) => (
                <Button
                  key={index}
                  type="button"
                  variant={action.variant || "outline"}
                  size="sm"
                  onClick={() => {
                    action.onClick(data);
                    if (action.closeAfter !== false) {
                      onClose();
                    }
                  }}
                  disabled={action.disabled}
                  className={action.className || ""}
                >
                  {action.icon && <span style={{ marginRight: '6px' }}>{action.icon}</span>}
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DetailDialog;
